/**
 * Get the number of days in a month
 * @param month - Month index (0-11)
 * @param year - Year
 * @returns Number of days in the month
 */
export function getDaysInMonth(month: number, year: number): number {
  return new Date(year, month + 1, 0).getDate();
}

/**
 * Check if a date is in the past
 * @param date - Date to check
 * @returns True if the date is in the past, false otherwise
 */
export function isDateInPast(date: Date): boolean {
  const now = new Date();
  // Reset hours, minutes, seconds, and milliseconds for accurate date comparison
  now.setHours(0, 0, 0, 0);
  const compareDate = new Date(date);
  compareDate.setHours(0, 0, 0, 0);
  return compareDate < now;
}

/**
 * Get an array of years, starting from the current year and going back
 * @param count - Number of years to include
 * @returns Array of years
 */
export function getYearsArray(count: number = 100): number[] {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: count }, (_, i) => currentYear - i);
}

/**
 * Get an array of days in a month
 * @param month - Month index (0-11)
 * @param year - Year
 * @returns Array of days
 */
export function getDaysArray(month: number, year: number): number[] {
  const daysInMonth = getDaysInMonth(month, year);
  return Array.from({ length: daysInMonth }, (_, i) => i + 1);
}

/**
 * Filter days array to exclude past days if needed
 * @param days - Array of days
 * @param year - Year
 * @param month - Month index (0-11)
 * @param disablePast - Whether to disable past dates
 * @returns Filtered array of days
 */
export function filterDaysForPast(
  days: number[],
  year: number,
  month: number,
  disablePast: boolean
): number[] {
  if (!disablePast) {
    return days;
  }
  
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();
  const currentDay = now.getDate();
  
  if (year === currentYear && month === currentMonth) {
    return days.filter(d => d >= currentDay);
  }
  
  return days;
}

/**
 * Get month names
 * @returns Array of month names
 */
export function getMonthNames(): string[] {
  return [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
}

/**
 * Filter months array to exclude past months if needed
 * @param months - Array of month objects with name and index
 * @param year - Year
 * @param disablePast - Whether to disable past dates
 * @returns Filtered array of month objects
 */
export function filterMonthsForPast(
  months: Array<{ m: string; i: number }>,
  year: number,
  disablePast: boolean
): Array<{ m: string; i: number }> {
  if (!disablePast) {
    return months;
  }
  
  const now = new Date();
  const currentYear = now.getFullYear();
  
  if (year < currentYear) {
    return [];
  }
  
  if (year === currentYear) {
    const currentMonth = now.getMonth();
    return months.filter(({ i }) => {
      return i === currentMonth || i > currentMonth;
    });
  }

  return months;
}

/**
 * Filter years array to exclude past years if needed
 * @param years - Array of years
 * @param disablePast - Whether to disable past dates
 * @returns Filtered array of years
 */
export function filterYearsForPast(
  years: number[],
  disablePast: boolean
): number[] {
  if (!disablePast) {
    return years;
  }
  
  const currentYear = new Date().getFullYear();
  return years.filter(y => y >= currentYear);
}